// src/SetupWizard/Step1Topic.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import { getApiUrl } from '../config';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

function Step1Topic({ hasExistingBlog, blogDomain, cmsPlatform, blogTopic, onChange, onImportStart, onImportEnd }) {
  const [existingBlog, setExistingBlog] = useState(hasExistingBlog || false);
  const [domain, setDomain] = useState(blogDomain || '');
  const [cms, setCms] = useState(cmsPlatform || '');
  const [topic, setTopic] = useState(blogTopic || '');
  const [topicError, setTopicError] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [domainError, setDomainError] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const [importError, setImportError] = useState('');
  const [importedCount, setImportedCount] = useState(0);

  useEffect(() => {
    // Make sure topic is correctly initialized if it changes in props
    if (blogTopic !== topic) {
      setTopic(blogTopic);
    }
  }, [blogTopic]);

  // Handle the change for existing blog question
  const handleExistingBlogChange = (e) => {
    const hasBlog = e.target.value === 'yes';
    setExistingBlog(hasBlog);
    
    // Clear domain and CMS if user changes to 'No'
    if (!hasBlog) {
      setDomain('');
      setCms('');
      setDomainError(false);
      onChange({ 
        hasExistingBlog: false,
        blogDomain: '',
        cmsPlatform: '' 
      });
    } else {
      onChange({ hasExistingBlog: true });
    }
  };

  // Handle the domain change with validation
  const handleDomainChange = (e) => {
    const newDomain = e.target.value;
    setDomain(newDomain);
    
    // Only set error if there's a value and it's invalid
    const isInvalid = newDomain && !validateDomain(newDomain);
    setDomainError(isInvalid);
    
    // Only update parent if domain is valid or empty
    if (!isInvalid) {
      onChange({ 
        blogDomain: newDomain,
        hasExistingBlog: existingBlog 
      });
    }
  };

  // Handle the CMS change
  const handleCmsChange = (e) => {
    const newCms = e.target.value;
    setCms(newCms);
    onChange({ cmsPlatform: newCms });
  };

  // Handle the topic change
  const handleTopicChange = (e) => {
    const newTopic = e.target.value;
    setTopic(newTopic);
    
    // Validate topic
    if (!newTopic.trim()) {
      setTopicError('Blog topic is required');
    } else if (newTopic.trim().length < 3) {
      setTopicError('Blog topic must be at least 3 characters');
    } else {
      setTopicError('');
    }
    
    onChange({ 
      topic: newTopic,
      isValid: newTopic.trim().length >= 3 // Pass validation state to parent
    });
  };

  const handleImportPosts = async () => {
    setIsImporting(true);
    setImportError('');
    setImportStatus('Preparing to import posts...');
    onImportStart();

    try {
      const token = localStorage.getItem('token');
      
      let cleanDomain = domain.trim()
        .toLowerCase()
        .replace(/^(https?:\/\/)?(www\.)?/, '')
        .replace(/\/$/, '');
      
      setImportStatus('Connecting to your blog...');
      const response = await axios.post(
        `${getApiUrl()}/posts/import-existing`,
        {
          blog_url: cleanDomain,
          cms_platform: cms
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // Update status with the success message from the API
      setImportStatus(response.data.message);
      setIsImporting(false);

      // Extract the number of posts from the message
      const importedCount = parseInt(response.data.message.match(/\d+/)[0]);
      
      // Update parent component with import success
      onChange({ 
        hasExistingBlog: true,
        blogDomain: cleanDomain,
        cmsPlatform: cms,
        importSuccess: true,
        importedCount: importedCount
      });

    } catch (error) {
      console.error('Error:', error);
      setImportError(error.response?.data?.error || 'Failed to import posts. Please try again.');
      setIsImporting(false);
    } finally {
      onImportEnd();
    }
  };

  // Add validation for the domain input
  const validateDomain = (value) => {
    if (!value) return true; // Empty value is valid (but will be caught by form validation)
    const cleanDomain = value.trim()
      .toLowerCase()
      .replace(/^(https?:\/\/)?(www\.)?/, '')
      .replace(/\/$/, '');
    
    // More permissive domain regex that allows subdomains and various TLDs
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-_.]*\.[a-zA-Z]{2,}$/;
    return domainRegex.test(cleanDomain);
  };

  return (
    <div>
      <h3>Step 1: Let's Get Started with Your Blog</h3>

      {/* Blog Topic Input - Now with Material-UI and validation */}
      <Box sx={{ width: '100%', mb: 3 }}>
        <TextField
          label="What would you like to blog about?"
          value={topic}
          onChange={handleTopicChange}
          error={!!topicError}
          helperText={topicError || "Choose a topic you're passionate about and can provide value to readers"}
          fullWidth
          margin="normal"
          required
          placeholder="e.g., Healthy Cooking, Travel Adventures, Personal Finance"
        />
        
        <Box sx={{ mt: 1, bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
          <p style={{ fontWeight: 'bold', marginTop: 0 }}>Tips for choosing a topic:</p>
          <ul style={{ marginBottom: 0 }}>
            <li>Pick something you're passionate about</li>
            <li>Consider your expertise or experience</li>
            <li>Think about what value you can provide to readers</li>
          </ul>
        </Box>
      </Box>

      {/* Existing Blog Question - Now with better context */}
      <div className="blog-type-section">
        <p>Do you already have a blog you'd like to import content from?</p>
        <div className="radio-options">
          <label className="radio-label">
            <input
              type="radio"
              value="no"
              checked={existingBlog === false}
              onChange={handleExistingBlogChange}
            />
            <span>No, I'm starting fresh</span>
            {!existingBlog && (
              <small className="option-description">
                Perfect! We'll help you create your blog from scratch.
              </small>
            )}
          </label>
          
          <label className="radio-label">
            <input
              type="radio"
              value="yes"
              checked={existingBlog === true}
              onChange={handleExistingBlogChange}
            />
            <span>Yes, I have an existing blog</span>
            {existingBlog && (
              <small className="option-description">
                Great! We can import your existing content to maintain consistency.
              </small>
            )}
          </label>
        </div>
      </div>

      {/* Follow-up Questions if the user has an existing blog */}
      {existingBlog && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <TextField
            label="Blog Domain"
            value={domain}
            onChange={handleDomainChange}
            error={domainError}
            helperText={domainError ? "Please enter a valid domain (e.g., example.com)" : "Enter your blog domain without http:// or www"}
            fullWidth
            margin="normal"
            disabled={isImporting}
            required={existingBlog}
            placeholder="example.com"
          />

          <TextField
            select
            label="CMS Platform"
            value={cms}
            onChange={handleCmsChange}
            fullWidth
            margin="normal"
            disabled={isImporting}
            required={existingBlog}
          >
            <MenuItem value="">Select your CMS</MenuItem>
            <MenuItem value="WordPress">WordPress</MenuItem>
            <MenuItem value="Wix">Wix</MenuItem>
            <MenuItem value="Squarespace">Squarespace</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </TextField>

          {(isImporting || importStatus) && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <Alert 
                severity={isImporting ? "info" : "success"}
                icon={isImporting ? undefined : <CheckCircleIcon />}
              >
                {importStatus}
              </Alert>
            </Box>
          )}

          {importError && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">{importError}</Alert>
            </Box>
          )}

          <LoadingButton
            onClick={handleImportPosts}
            loading={isImporting}
            disabled={isImporting || !domain || !cms || domainError}
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
          >
            {isImporting ? 'Importing...' : 'Import Posts'}
          </LoadingButton>
        </Box>
      )}
    </div>
  );
}

export default Step1Topic;
